import { ClientRoleEnum } from './client-role.enum';

/** Cap for an admin-composed free-text message to a customer (one SMS-ish length budget). */
export const CUSTOMER_MESSAGE_MAX_LENGTH = 400;

export interface IGoogleAuth {
  refreshToken: string;
  scopes: string[]
}

export interface IAuth{
  google?: IGoogleAuth;
}

/** Which calendar layout an employee lands on. Mirrors the admin calendar's own views. */
export type CalendarDefaultView = 'day' | 'week' | 'list';
/** Whose appointments that calendar starts scoped to. */
export type CalendarDefaultScope = 'self' | 'branch';

export interface ICalendarDefaults {
  view?: CalendarDefaultView;
  /** `branch` only takes effect for someone allowed to see the branch calendar. */
  scope?: CalendarDefaultScope;
}

export interface IClient {
  userId: string;
  phoneNumber?: string;
  fullName: string;
  email: string;
  bid?: string;
  auth?: IAuth;
  notificationUserId?: Array<string>;
  active: boolean;
  profileImage?: string;
  referer?: string;
  role?: ClientRoleEnum;
  /**
   * Per-employee privacy gate. When explicitly `false`, the server strips customer
   * phone numbers and e-mail addresses from every response this user receives — they
   * can still message customers, but through server-side sends only.
   * Undefined means "allowed" so existing employees are unaffected. Managers
   * (admin / operator / mainClient) always see contact details regardless.
   */
  canViewCustomerContact?: boolean;
  /**
   * Lets a plain employee see the whole branch's calendar — their own appointments
   * plus every other employee working at the same branch(es) — the way a manager
   * does. Purely an authorization flag: it widens what they may read, nothing else.
   * Undefined / false => they only ever see their own calendar.
   */
  canViewBranchCalendar?: boolean;
  /**
   * What this employee's calendar opens on, set by the owner. Only a starting point:
   * once they switch view or staff filter themselves, their own choice is what sticks.
   */
  calendarDefaults?: ICalendarDefaults;
  lastSeenAnnouncementVersion?: string;
  appVersion?: string; // Last known app version reported by the client
  appVersionUpdatedAt?: Date; // When appVersion was last updated
}
